import React, { createContext, useContext, useState, useEffect } from "react";
import { getLPSettings, getConfig, BASE_URL } from "../services/api";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const DEFAULT_THEME = {
  primary: "#10b981",
  hover: "#059669",
  light: "#d1fae5",
  dark: "#047857",
};

export const ThemeProvider = ({ children }) => {
  const [themeColor, setThemeColor] = useState(DEFAULT_THEME.primary);
  const [themeHover, setThemeHover] = useState(DEFAULT_THEME.hover);
  const [themeLight, setThemeLight] = useState(DEFAULT_THEME.light);
  const [themeDark, setThemeDark] = useState(DEFAULT_THEME.dark);
  const [orgName, setOrgName] = useState("");
  const [orgAlamat, setOrgAlamat] = useState("");
  const [orgTelepon, setOrgTelepon] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [footerSettings, setFooterSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadBranding = async () => {
      setLoading(true);

      const [settingsResult, configResult] = await Promise.allSettled([
        getLPSettings(),
        getConfig(),
      ]);

      if (cancelled) return;

      if (settingsResult.status === "fulfilled" && settingsResult.value?.data) {
        const data = settingsResult.value.data;
        setFooterSettings(data);
        setOrgName(data.nama_lembaga || "");
        const alamat = [data.alamat, data.desa, data.kode_pos]
          .map((v) => (v || "").toString().trim())
          .filter(Boolean)
          .join(", ");
        setOrgAlamat(alamat);
        setOrgTelepon(data.no_hp?.trim() || "");
        setThemeColor(data.warna_tema || DEFAULT_THEME.primary);
        setThemeHover(data.warna_tema_hover || DEFAULT_THEME.hover);
        setThemeLight(data.warna_tema_light || DEFAULT_THEME.light);
        setThemeDark(data.warna_tema_dark || DEFAULT_THEME.dark);
        applyThemeColors({
          primary: data.warna_tema || DEFAULT_THEME.primary,
          hover: data.warna_tema_hover || DEFAULT_THEME.hover,
          light: data.warna_tema_light || DEFAULT_THEME.light,
          dark: data.warna_tema_dark || DEFAULT_THEME.dark,
        });
      } else {
        applyThemeColors(DEFAULT_THEME);
      }

      if (configResult.status === "fulfilled") {
        const logoPath = configResult.value?.data?.LOGO_URL;
        setLogoUrl(logoPath ? `${BASE_URL}${logoPath}` : "");
      } else {
        setLogoUrl("");
      }

      setLoading(false);
    };

    loadBranding();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (orgName) {
      document.title = orgName;
    }
  }, [orgName]);

  const hexToRgb = (hex) => {
    let cleanHex = hex.replace("#", "");
    if (cleanHex.length === 3) {
      cleanHex = cleanHex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    const bigint = parseInt(cleanHex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `${r}, ${g}, ${b}`;
  };

  const applyThemeColors = ({ primary, hover, light, dark }) => {
    const primaryRgb = hexToRgb(primary);
    const hoverRgb = hexToRgb(hover);
    const darkRgb = hexToRgb(dark);
    document.documentElement.style.setProperty("--primary-color", primary);
    document.documentElement.style.setProperty("--primary-hover", hover);
    document.documentElement.style.setProperty("--primary-light", light);
    document.documentElement.style.setProperty("--primary-dark", dark);
    document.documentElement.style.setProperty("--primary-rgb", primaryRgb);
    document.documentElement.style.setProperty("--primary-hover-rgb", hoverRgb);
    document.documentElement.style.setProperty("--primary-dark-rgb", darkRgb);
    document.documentElement.style.setProperty(
      "--primary-overlay",
      `rgba(${primaryRgb}, 0.9)`,
    );
    document.documentElement.style.setProperty(
      "--primary-hover-overlay",
      `rgba(${hoverRgb}, 0.9)`,
    );
  };

  const updateThemeColor = ({ primary, hover, light, dark }) => {
    setThemeColor(primary);
    setThemeHover(hover);
    setThemeLight(light);
    setThemeDark(dark);
    applyThemeColors({ primary, hover, light, dark });
  };

  const fetchLpSettings = async () => {
    try {
      const response = await getLPSettings();
      if (response.data) {
        setFooterSettings(response.data);
        setOrgName(response.data.nama_lembaga || "");
        const alamat = [response.data.alamat, response.data.desa, response.data.kode_pos]
          .map((v) => (v || "").toString().trim())
          .filter(Boolean)
          .join(", ");
        setOrgAlamat(alamat);
        setOrgTelepon(response.data.no_hp?.trim() || "");
        setThemeColor(response.data.warna_tema || DEFAULT_THEME.primary);
        setThemeHover(response.data.warna_tema_hover || DEFAULT_THEME.hover);
        setThemeLight(response.data.warna_tema_light || DEFAULT_THEME.light);
        setThemeDark(response.data.warna_tema_dark || DEFAULT_THEME.dark);
        applyThemeColors({
          primary: response.data.warna_tema || DEFAULT_THEME.primary,
          hover: response.data.warna_tema_hover || DEFAULT_THEME.hover,
          light: response.data.warna_tema_light || DEFAULT_THEME.light,
          dark: response.data.warna_tema_dark || DEFAULT_THEME.dark,
        });
      }
    } catch (error) {
      applyThemeColors(DEFAULT_THEME);
    }
  };

  const fetchLogo = async () => {
    try {
      const { data } = await getConfig();
      setLogoUrl(data?.LOGO_URL ? `${BASE_URL}${data.LOGO_URL}` : "");
    } catch (error) {
      setLogoUrl("");
    }
  };

  const refreshTheme = async () => {
    setLoading(true);
    await Promise.all([fetchLpSettings(), fetchLogo()]);
    setLoading(false);
  };

  const value = {
    themeColor,
    themeHover,
    themeLight,
    themeDark,
    orgName,
    footerSettings,
    orgAlamat,
    orgTelepon,
    logoUrl,
    updateThemeColor,
    refreshTheme,
    refreshLogo: fetchLogo,
    loading,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
